import { Link } from "react-router-dom";
import { Flame, Instagram, MessageCircle, MapPin } from "lucide-react";
import { useState, useEffect } from "react";
import { api } from "../lib/api";
import { toast } from "sonner";

export default function Footer() {
  const [email, setEmail] = useState("");
  const [whatsapp, setWhatsapp] = useState("");

  useEffect(() => {
    api.get("/config").then(({ data }) => setWhatsapp(data.whatsapp)).catch(() => {});
  }, []);

  const subscribe = async (e) => {
    e.preventDefault();
    if (!email) return;
    try {
      await api.post("/newsletter", { email });
      toast.success("You're on the list. Welcome home.");
      setEmail("");
    } catch (err) {
      toast.error("Could not subscribe. Please try again.");
    }
  };

  return (
    <footer className="bg-[#1A1A1A] text-[#FDFBF7] mt-24" data-testid="site-footer">
      <div className="max-w-7xl mx-auto px-6 lg:px-10 py-16 grid grid-cols-1 md:grid-cols-12 gap-10">
        <div className="md:col-span-5">
          <div className="flex items-center gap-3 mb-6">
            <img
              src="https://customer-assets-jt897jd0.emergentagent.net/job_scent-studio-94/artifacts/5yodkusi_unnamed.png"
              alt="Ave Luxe Candles"
              className="w-16 h-16 object-contain rounded-full bg-[#FDFBF7]/95 p-0.5"
            />
            <div>
              <div className="font-serif-display text-3xl">Ave Luxe</div>
              <div className="text-[10px] uppercase tracking-widest-plus text-[#9BA595] -mt-1">bespoke gifting</div>
            </div>
          </div>
          <p className="font-serif-display text-2xl leading-snug mb-6 max-w-md">
            Hand-poured with love, to brighten your home — and your heart.
          </p>
          <form onSubmit={subscribe} className="flex gap-2 max-w-sm">
            <input
              type="email"
              required
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="Enter your email"
              className="flex-1 bg-transparent border border-[#3a3a3a] px-4 py-3 text-sm rounded-full outline-none focus:border-[#D27D60] transition-colors"
              data-testid="footer-newsletter-email"
            />
            <button
              type="submit"
              className="bg-[#D27D60] text-[#FDFBF7] px-5 py-3 text-sm rounded-full hover:bg-[#c46e51] transition-colors"
              data-testid="footer-newsletter-submit"
            >
              Subscribe
            </button>
          </form>
        </div>

        <div className="md:col-span-2">
          <div className="text-[11px] uppercase tracking-widest-plus text-[#9BA595] mb-5">Shop</div>
          <ul className="space-y-3 text-sm">
            <li><Link to="/shop/candles" className="hover:text-[#D27D60] transition-colors" data-testid="footer-link-candles">Candles</Link></li>
            <li><Link to="/shop/figurines" className="hover:text-[#D27D60] transition-colors" data-testid="footer-link-figurines">Figurines</Link></li>
            <li><Link to="/shop/diy-kits" className="hover:text-[#D27D60] transition-colors" data-testid="footer-link-diy">DIY Kits</Link></li>
            <li><Link to="/shop/floral-bouquets" className="hover:text-[#D27D60] transition-colors" data-testid="footer-link-bouquets">Bouquets</Link></li>
            <li><Link to="/shop/home-decor" className="hover:text-[#D27D60] transition-colors" data-testid="footer-link-decor">Home Decor</Link></li>
            <li><Link to="/shop/garden-decor-accessories" className="hover:text-[#D27D60] transition-colors" data-testid="footer-link-garden">Garden Décor</Link></li>
            <li><Link to="/build-bouquet" className="hover:text-[#D27D60] transition-colors" data-testid="footer-link-bouquet">Build a Bouquet</Link></li>
          </ul>
        </div>

        <div className="md:col-span-2">
          <div className="text-[11px] uppercase tracking-widest-plus text-[#9BA595] mb-5">Company</div>
          <ul className="space-y-3 text-sm">
            <li><Link to="/about" className="hover:text-[#D27D60] transition-colors" data-testid="footer-link-about">Our Story</Link></li>
            <li><Link to="/contact" className="hover:text-[#D27D60] transition-colors" data-testid="footer-link-contact">Contact</Link></li>
            <li><Link to="/track-order" className="hover:text-[#D27D60] transition-colors" data-testid="footer-link-track">Track Order</Link></li>
            <li><Link to="/admin" className="hover:text-[#D27D60] transition-colors" data-testid="footer-link-admin">Admin</Link></li>
          </ul>
        </div>

        <div className="md:col-span-3">
          <div className="text-[11px] uppercase tracking-widest-plus text-[#9BA595] mb-5">Visit us</div>
          <div className="text-sm space-y-3">
            <div className="flex items-start gap-2">
              <MapPin className="w-4 h-4 mt-0.5 text-[#9BA595]" strokeWidth={1.5} />
              <span>Mourigram, Howrah,<br />West Bengal 711109</span>
            </div>
            {whatsapp && (
              <a
                href={`https://wa.me/${whatsapp.replace(/[^0-9]/g, "")}`}
                target="_blank"
                rel="noreferrer"
                className="flex items-center gap-2 hover:text-[#D27D60] transition-colors"
                data-testid="footer-whatsapp"
              >
                <MessageCircle className="w-4 h-4 text-[#9BA595]" strokeWidth={1.5} />
                WhatsApp us
              </a>
            )}
            <a
              href="https://instagram.com/ave_luxe_candles"
              target="_blank"
              rel="noreferrer"
              className="flex items-center gap-2 hover:text-[#D27D60] transition-colors"
              data-testid="footer-instagram"
            >
              <Instagram className="w-4 h-4 text-[#9BA595]" strokeWidth={1.5} />
              @ave_luxe_candles
            </a>
          </div>
        </div>
      </div>

      <div className="border-t border-[#2a2a2a]">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 py-6 flex flex-col md:flex-row items-center justify-between text-xs text-[#9BA595] gap-2">
          <div>© {new Date().getFullYear()} Ave Luxe · Bespoke gifting. All rights reserved.</div>
          <div className="tracking-widest-plus uppercase">Made in Howrah · with love</div>
        </div>
      </div>
    </footer>
  );
}
